'use client'

import type { LoginFormProps } from '@maestro/commerce-blocks/components/login/login.types'

import { useTranslations } from 'next-intl'
import React from 'react'

import { LanguageSwitcher } from '@/components/language-switcher'
import { ForgotPassword } from '@maestro/commerce-blocks/components/forgot-password'
import { useLoginForm } from '@maestro/commerce-blocks/components/login/form/batons/use-login-form'
import { VippsButton } from '@maestro/commerce-blocks/components/vipps-button'
import { theme } from '@maestro/local/theme'
import { Button } from '@pearlconvert/ui/components/button'
import { ErrorMessage } from '@pearlconvert/ui/components/error-message'
import { Fieldset } from '@pearlconvert/ui/components/fieldset'
import { Input } from '@pearlconvert/ui/components/input'

export default function LoginForm({
  cmsBlock,
  cmsBlockForgotPassword,
  isDistributorDomain,
}: LoginFormProps) {
  const t = useTranslations('commerce-blocks')
  const { showVippsButton } = theme().loginPage

  const {
    formT,
    isSubmitting,
    isSubmitDisabled,
    handleSubmit,
    errors,
    register,
    fieldErrors,
  } = useLoginForm()

  return (
    <div className="login-form-wrapper">
      {cmsBlock && <div className="login-form__cms-block">{cmsBlock}</div>}
      {showVippsButton && (
        <>
          <VippsButton ariaLabel={t('auth.vipps.login-ariaLabel')}>
            {t('auth.vipps.login')}
          </VippsButton>
          <div className="section-header login-form__separator">
            <span>{t('auth.login.or-login')}</span>
          </div>
        </>
      )}
      <form onSubmit={handleSubmit} className="login-form">
        <Fieldset legend={t('auth.login.form-title')}>
          {!isDistributorDomain && (
            <div className="login-form__language-switcher">
              <LanguageSwitcher
                showLabel
                label={formT('language.label')}
                size="large"
              />
            </div>
          )}
          <Input
            id="email"
            {...register('email')}
            type="email"
            label={formT('email.label')}
            placeholder={formT('email.placeholder')}
            autoComplete="email"
            error={errors.email?.message}
          />
          <Input
            id="password"
            {...register('password')}
            type="password"
            label={formT('password.label')}
            placeholder={formT('password.placeholder')}
            autoComplete="current-password"
            error={errors.password?.message}
          />
          <ForgotPassword cmsBlock={cmsBlockForgotPassword} />
          <ErrorMessage
            errors={fieldErrors}
            errorMessageHeader={t('auth.login.error-message-header')}
          >
            {errors.root?.serverError?.message}
          </ErrorMessage>
          <Button
            disabled={isSubmitDisabled}
            isLoading={isSubmitting}
            type="primary"
            buttonType="submit"
          >
            {t('auth.login.title')}
          </Button>
        </Fieldset>
      </form>
    </div>
  )
}
